import { useState, useRef, useEffect, cloneElement } from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";
import { EXPORT_IGNORE_ATTRIBUTE } from "../../hooks/useExportGraphsToPptx";

const Tooltip = ({
    children,
    content,
    tooltipId,
    tooltipClassName,
    arrowClassName,
    wrapperClassName,
    portal,
    placement,
    offset,
    icon,
    iconButtonStyle,
    iconClassName,
    iconAriaLabel,
    iconColor,
    iconPaddingLeft,
}) => {
    const triggerRef = useRef(null);
    const tooltipRef = useRef(null);
    const [isOpen, setIsOpen] = useState(false);
    const [position, setPosition] = useState({ top: 0, left: 0 });

    const computePosition = (targetElement) => {
        if (!portal || !targetElement) return;

        const rect = targetElement.getBoundingClientRect();

        if (placement === "bottom") {
            setPosition({
                top: rect.bottom + window.scrollY + offset,
                left: rect.left + window.scrollX,
            });
            return;
        }

        setPosition({
            top: rect.top + window.scrollY + rect.height / 2,
            left: rect.right + window.scrollX + offset,
        });
    };

    const openTooltip = (event) => {
        computePosition(event?.currentTarget || triggerRef.current);
        setIsOpen(true);
    };

    const closeTooltip = (event) => {
        const nextTarget = event?.relatedTarget;

        if (
            nextTarget &&
            (triggerRef.current?.contains(nextTarget) ||
                tooltipRef.current?.contains(nextTarget))
        ) {
            return;
        }

        setIsOpen(false);
    };

    useEffect(() => {
        if (!isOpen) return;

        const handleEsc = (event) => {
            if (event.key === "Escape") {
                setIsOpen(false);
            }
        };

        document.addEventListener("keydown", handleEsc);

        return () => {
            document.removeEventListener("keydown", handleEsc);
        };
    }, [isOpen]);

    const child =
        children ||
        (icon ? (
            <button
                type="button"
                style={iconButtonStyle}
                aria-label={iconAriaLabel}
                className={iconClassName}
            >
                <i
                    className="fa-solid fa-circle-info"
                    style={{
                        color: iconColor,
                        paddingLeft: iconPaddingLeft,
                        cursor: "pointer",
                    }}
                />
            </button>
        ) : null);

    if (!child) {
        return null;
    }

    const mergedProps = {
        ref: (node) => {
            triggerRef.current = node;
            if (typeof child.ref === "function") {
                child.ref(node);
            }
        },
        "aria-describedby": tooltipId,
        onMouseEnter: (event) => {
            child.props.onMouseEnter?.(event);
            openTooltip(event);
        },
        onMouseLeave: (event) => {
            child.props.onMouseLeave?.(event);
            closeTooltip(event);
        },
        onFocus: (event) => {
            child.props.onFocus?.(event);
            openTooltip(event);
        },
        onBlur: (event) => {
            child.props.onBlur?.(event);
            closeTooltip(event);
        },
        onKeyDown: (event) => {
            child.props.onKeyDown?.(event);
            if (event.key === "Escape") {
                setIsOpen(false);
            }
        },
    };

    const tooltipNode = isOpen ? (
        <div
            ref={tooltipRef}
            className={tooltipClassName}
            role="tooltip"
            id={tooltipId}
            style={
                portal ? { top: position.top, left: position.left } : undefined
            }
            onMouseEnter={() => setIsOpen(true)}
            onMouseLeave={closeTooltip}
        >
            {content}
            {arrowClassName && <div className={arrowClassName} />}
        </div>
    ) : null;

    return (
        <span
            className={wrapperClassName}
            style={{ position: "relative", display: "inline-block" }}
            {...{
                [EXPORT_IGNORE_ATTRIBUTE]: "true",
            }}
        >
            {cloneElement(child, mergedProps)}
            {portal
                ? tooltipNode &&
                  ReactDOM.createPortal(tooltipNode, document.body)
                : tooltipNode}
        </span>
    );
};

Tooltip.propTypes = {
    children: PropTypes.element,
    content: PropTypes.node.isRequired,
    tooltipId: PropTypes.string,
    tooltipClassName: PropTypes.string,
    arrowClassName: PropTypes.string,
    wrapperClassName: PropTypes.string,
    portal: PropTypes.bool,
    placement: PropTypes.oneOf(["right", "bottom"]),
    offset: PropTypes.number,
    icon: PropTypes.bool,
    iconButtonStyle: PropTypes.object,
    iconClassName: PropTypes.string,
    iconAriaLabel: PropTypes.string,
    iconColor: PropTypes.string,
    iconPaddingLeft: PropTypes.number,
};

Tooltip.defaultProps = {
    tooltipId: undefined,
    tooltipClassName: "reporting-period-tooltip",
    arrowClassName: "tooltipArrow",
    wrapperClassName: undefined,
    portal: false,
    placement: "right",
    offset: 15,
    children: null,
    icon: false,
    iconButtonStyle: {
        background: "transparent",
        border: 0,
        padding: 0,
    },
    iconClassName: undefined,
    iconAriaLabel: "More information",
    iconColor: "#015294",
    iconPaddingLeft: 4,
};

export default Tooltip;
